import { useEffect, useEffectEvent } from "react";
import { useTranslation } from "react-i18next";
import NavigationBar from "../../../components/layout/NavigationBar";
import CtaSection from "../../../components/home/CtaSection";
import VenueDetailHero from "../../../components/venues/VenueDetailHero";
import VenueDetailMandateImage from "../../../components/venues/VenueDetailMandateImage";
import VenueDetailSpecs from "../../../components/venues/VenueDetailSpecs";
import ExclusivelyCrafted from "../../../components/home/ExclusivelyCrafted";
import StorySlider from "../../../components/about/StorySlider";
import Testimonial from "../../../components/home/Testimonial";
import ScheduleIntro from "../../../components/schedule/ScheduleIntro";
import ScheduleList from "../../../components/schedule/ScheduleList";
import ExperienceCollection from "../../../components/home/ExperienceCollection";
import VenuesGrid from "../../../components/venues/VenuesGrid";
import GiftSection from "../../../components/home/GiftSection";
import DemoRibbon from "../../../components/common/DemoRibbon";
import { SlotContext } from "../../SlotContext";
import { buildClassicModel } from "./buildClassicModel";

/**
 * The classic (editorial) invitation — the Webflow design guests have always
 * been sent.
 *
 * Props:
 *   event        the public invitation payload; null while it loads
 *   preview      drawn inside the couple's editor: placeholders for missing
 *                details, every section marked so it can be pointed at, no
 *                language switch, no demo ribbon
 *   editable     (preview) the couple may type on the template's lines
 *   revealed     the page has finished loading (the demo ribbon waits for it)
 *   links        guests' page only: { joinUrl, rsvpCode, eventId }
 *   onTextInput  (preview) (slot, value) — a line typed on the page
 *   onRendered   ({ sections, layoutKey }) — after each new payload is drawn
 */
const ClassicInvitation = ({
  event,
  preview = false,
  editable = false,
  revealed = false,
  links = null,
  onTextInput,
  onRendered,
}) => {
  const { t } = useTranslation();
  const model = buildClassicModel(event, t, { preview });
  const { sections, lines, placeholders } = model;
  const has = (key) => sections.includes(key);
  // The editor finds a section (to scroll to it, or to learn which one the
  // couple clicked) by this mark. Guests' markup carries none.
  const mark = (key) => (preview ? { "data-invitation-section": key } : {});

  const slots = { texts: model.texts, editable: preview && editable, onTextInput };

  // Turning editing on or off swaps every line for a new element, which the
  // Webflow runtime has to be re-bound to like a new section.
  const reportRendered = useEffectEvent(() => {
    onRendered?.({
      sections,
      layoutKey: `${model.layoutKey}|${slots.editable ? "editable" : "read"}`,
    });
  });
  useEffect(() => {
    if (event) reportRendered();
  }, [event]);

  const missingVenues = [
    placeholders.ceremony && t("preview.placeholder.ceremony"),
    placeholders.reception && t("preview.placeholder.reception"),
  ].filter(Boolean);

  return (
    <SlotContext.Provider value={slots}>
      <NavigationBar sections={sections} showLanguageSwitch={!preview} />

      <div className="main" style={{ opacity: 0 }}>
        {event && (
          <>
            {!preview && event.isDemo && <DemoRibbon show={revealed} />}
            <VenueDetailHero
              id="the-couple"
              partner_one={event.partner1FirstName}
              partner_two={event.partner2FirstName}
              location={event.location}
              namesPlaceholder={placeholders.names ? t("preview.placeholder.names") : null}
              locationPlaceholder={
                placeholders.location ? t("preview.placeholder.location") : null
              }
              {...mark("hero")}
            />
            <VenueDetailMandateImage
              mainImage={event.mainImage || "/assets/photos/agapae-hero.jpg"}
              date={
                event.dateISO
                  ? event.dateISO.slice(8, 10) + "/" + event.dateISO.slice(5, 7)
                  : ""
              }
              eventType={event.eventType}
              {...mark("invitation")}
            />
            {has("participants") && (
              <VenueDetailSpecs
                participantNames={model.participantNames}
                participantImage={
                  event.participantImage || "/assets/images/invitation-section.jpg"
                }
                {...mark("participants")}
              />
            )}
            {has("interlude") && (
              <ExclusivelyCrafted hideTopLines={!lines.interludeTop} {...mark("interlude")} />
            )}
            {has("story") && (
              <StorySlider
                id="our-story"
                slides={model.storySlides}
                showTopLine={lines.storyTop}
                {...mark("story")}
              />
            )}
            {has("quote") && (
              <Testimonial hideTopLines={!lines.quoteTop} {...mark("quote")} />
            )}
            {model.venueItems.length > 0 && (
              <VenuesGrid id="where-and-when" items={model.venueItems} {...mark("venues")} />
            )}
            {missingVenues.length > 0 && (
              <section
                id={model.venueItems.length > 0 ? undefined : "where-and-when"}
                className="section topless"
                {...mark("venues")}
              >
                <div className="container">
                  {missingVenues.map((line) => (
                    <p key={line} className="made-28 invitation-placeholder">
                      {line}
                    </p>
                  ))}
                </div>
              </section>
            )}
            <section id="schedule" className="section topless" {...mark("schedule")}>
              <div
                data-w-id="9361a639-6f3b-5542-a7b0-564c768ebb2a"
                className="container"
              >
                <ScheduleIntro />
                {placeholders.schedule ? (
                  <p className="made-28 invitation-placeholder">
                    {t("preview.placeholder.schedule")}
                  </p>
                ) : (
                  <ScheduleList items={model.scheduleItems} />
                )}
              </div>
            </section>
            {has("moments") && (
              <ExperienceCollection
                id="gallery"
                joinUrl={links?.joinUrl}
                arcImage={event.momentsArcImage || ""}
                cardImages={[
                  event.momentsCard1Image || "",
                  event.momentsCard2Image || "",
                  event.momentsCard3Image || "",
                ]}
                {...mark("moments")}
              />
            )}
            {has("gifts") && (
              <GiftSection
                giftMessage={event.giftMessage}
                giftIban={event.giftIban}
                giftIbanHolder={event.giftIbanHolder}
                giftRegistryUrl={event.giftRegistryUrl}
                {...mark("gifts")}
              />
            )}
            {has("rsvp") && (
              <CtaSection
                id="rsvp"
                href={`${import.meta.env.VITE_APP_URL}/couple/create-event`}
                isRsvp={true}
                rsvpCode={links?.rsvpCode ?? null}
                eventId={links?.eventId ?? event.eventId}
                eventType={event.eventType}
                rsvpDeadline={event.rsvpDeadline}
                contacts={[
                  { name: event.partner1FirstName, phone: event.partner1Phone },
                  { name: event.partner2FirstName, phone: event.partner2Phone },
                ]}
                {...mark("rsvp")}
              />
            )}
          </>
        )}
      </div>
    </SlotContext.Provider>
  );
};

export default ClassicInvitation;
